import { EntityState } from '@ngrx/entity'
import { MemoizedSelector, createFeatureSelector, createSelector } from '@ngrx/store'
import { TreeNode } from 'primeng/api'

import {
  DataMapper,
  ResourceNodeSlice,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  DataModelUiEntity,
  TreeNodeData,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { computeDataModelTree } from '@flowx/designer/modules/data-model-builder/services/data-model-builder.utils'
import { WorkflowNodeType } from '@flowx/designer/modules/integration-designer/models/workflow.entity'

import { selectAllProjectDataModelEntities } from './data-model.selectors'

import { LocalAttributeStaging } from '../actions/data-mapping.actions'
import {
  DataMappingSourceState,
  DataMappingState,
  ResourceSlicesState,
  dataMappingFeatureKey,
  selectAllLocalDataMappings,
  selectAllResourceNodesSlices,
} from '../reducers/data-mapping.reducer'

export const selectDataMapping = createFeatureSelector<DataMappingState>(dataMappingFeatureKey)

export const selectResourceNodesSlices = createSelector(
  selectDataMapping,
  (state: DataMappingState) => state.resourceNodes
)

const selectResourceNodesSlicesByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, ResourceSlicesState> =>
  createSelector(selectResourceNodesSlices, (state) => state.entities[resourceId])

export const selectAllWorkflowEndNodeSlicesByResourceIds = (
  resourceIds: string[]
): MemoizedSelector<DataMappingState, Record<string, ResourceNodeSlice[]>> =>
  createSelector(selectResourceNodesSlices, (state) =>
    resourceIds.reduce(
      (acc, resourceId) => {
        const resourceSlicesState = state.entities[resourceId]
        if (resourceSlicesState) {
          const filteredNodeSlices = Object.values(resourceSlicesState.nodeSlices.entities).filter(
            (nodeSlice) => nodeSlice && nodeSlice.nodeType === WorkflowNodeType.END
          )
          if (filteredNodeSlices.length > 0) {
            acc[resourceId] = filteredNodeSlices as ResourceNodeSlice[]
          }
        }
        return acc
      },
      {} as Record<string, ResourceNodeSlice[]>
    )
  )

export const selectAllNodeSlicesByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, ResourceNodeSlice[]> =>
  createSelector(selectResourceNodesSlicesByResourceId(resourceId), (resourceSlicesState) => {
    if (!resourceSlicesState) {
      return []
    }
    return selectAllResourceNodesSlices(resourceSlicesState.nodeSlices)
  })

export const selectWorkflowStartNodeSlicesByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, ResourceNodeSlice> =>
  createSelector(selectResourceNodesSlicesByResourceId(resourceId), (resourceSlicesState) => {
    if (!resourceSlicesState) {
      return null
    }
    const allNodeSlices = selectAllResourceNodesSlices(resourceSlicesState.nodeSlices)
    const startNodeSlices = allNodeSlices.filter(
      (nodeSlice) => nodeSlice.nodeType === WorkflowNodeType.START
    )
    return startNodeSlices[0] ? startNodeSlices[0] : null
  })

export const selectNotificationTemplateSlicesByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, ResourceNodeSlice> =>
  createSelector(selectResourceNodesSlicesByResourceId(resourceId), (resourceSlicesState) => {
    if (!resourceSlicesState) {
      return null
    }
    const allNodeSlices = selectAllResourceNodesSlices(resourceSlicesState.nodeSlices)
    return allNodeSlices[0] ? allNodeSlices[0] : null
  })

export const selectDataEntitiesByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, DataModelUiEntity[]> =>
  createSelector(selectResourceNodesSlicesByResourceId(resourceId), (resourceSlicesState) => {
    if (!resourceSlicesState) {
      return []
    }
    return resourceSlicesState.dataEntities
  })

export const selectDataModelTreeByResourceId = (
  resourceId: string
): MemoizedSelector<DataMappingState, TreeNode<TreeNodeData>[]> =>
  createSelector(
    selectDataEntitiesByResourceId(resourceId),
    selectAllProjectDataModelEntities,
    (dataEntities, projectDataModelEntities) =>
      computeDataModelTree([...dataEntities, ...projectDataModelEntities]).tree
  )

export const selectActionDataMappingState = createSelector(
  selectDataMapping,
  (state: DataMappingState) => state.elementsMapping.data
)
export const selectActionDataMappingLoading = createSelector(
  selectDataMapping,
  (state: DataMappingState) => state.elementsMapping.loading
)

export const selectLocalDataMappers = createSelector(
  selectDataMapping,
  (state: DataMappingState) =>
    state.localActionDataMappers ? selectAllLocalDataMappings(state.localActionDataMappers) : []
)

export const selectTestMappingResponse = createSelector(
  selectDataMapping,
  (state: DataMappingState) => state.testMappingResponse?.data
)

export const selectTestMappingLoading = createSelector(
  selectDataMapping,
  (state: DataMappingState) => state.testMappingResponse?.loading
)

export const selectCurrentElementDataMappers = (
  elementFlowxUuid: string
): MemoizedSelector<DataMappingState, DataMapper[]> =>
  createSelector(selectActionDataMappingState, (state: EntityState<DataMappingSourceState>) => {
    const mappers = state.entities[elementFlowxUuid]?.nodesSliceMappings
    if (!mappers) {
      return []
    }
    return mappers
  })

export const selectCurrentElementDataMappersByNodeFlowxUuid = (
  nodeFlowxUuid: string,
  elementFlowxUuid: string
): MemoizedSelector<
  DataMappingState,
  { mapper: Partial<DataMapper> & LocalAttributeStaging; isLocal: boolean }
> =>
  createSelector(
    selectCurrentElementDataMappers(elementFlowxUuid),
    selectLocalDataMappers,
    (mappers, localMappers) => {
      const localNodeMapper = localMappers.find(
        (mapper) => mapper.targetElementId === nodeFlowxUuid
      )

      if (localNodeMapper) {
        return { mapper: localNodeMapper as Partial<DataMapper>, isLocal: true }
      }

      const nodeMapper = mappers.find((mapper) => mapper.targetElementId === nodeFlowxUuid)
      if (nodeMapper) {
        return { mapper: nodeMapper, isLocal: false }
      }
      return null
    }
  )
